import { userStatus } from 'common-state';
import { isBrowser } from 'common-state/src/helpers/utils';
import type {
  AnalyticPageInfo,
  AnalyticUserInfo,
  UserDetails,
  DLClickEventProps,
  UserPermissions,
  DLBasketUpdateProps,
  DLBasketDetailsProps,
  DLRibbonPromotionProps,
} from 'tsconfig/types';
import { globals } from '../utils/globals';
import { DL_EVENTS_CONFIGURATION } from './AdobeDataLayerEvents';
import AdobeDataLayerMappings from '../utils/AdobeDataLayerMappings';
import { normalizeEmail, normalizePhone, normalizeCountry, normalizeDOB, sha256Hash } from './identityNormalizer';

// This is the list of events that are allowed to be pushed to the data layer in embedded
const WHITE_LIST_EVENTS = [
  'mainNavClick',
  'topNavClick',
  'cartIcon',
  'search',
  'loginButton',
  'logout',
  'notYou',
  'phoneClick',
  'liveChatInteraction',
  'emailClick',
  'accordionMenu',
  'socialShare',
  'launchMiniCart',
  'miniCartClick',
  'basketUpdate',
  'basketDetails',
  'stateSelector',
  'ribbonClick',
];

const datalayerCleanup = new Set([
  ...WHITE_LIST_EVENTS,
  'favourite',
  'unfavourite',
  'addToCart',
  'InitiateCart',
  'unlimited auto-renewal opt-in',
  'unlimited auto-renewal opt-out',
  'removeFromCart',
  'pageView',
]);

export class AdobeDataLayer {
  private readonly tempAdobeDataLayer: any[] = [];
  private listenerKey: any;
  private readonly enableLogger: boolean;
  private currentUserData: UserDetails | undefined;
  private currentPermissionData: UserPermissions[] | undefined;

  // Constructor for the AdobeDataLayer class. Initializes the data layer and sets the logger status.
  constructor(enableLog = false) {
    this.listennerForWindowsDL();
    this.enableLogger = enableLog;
  }

  // Getter for the Adobe Data Layer from the window object. Returns an empty array if not available.
  get getWindowDL(): any {
    return isBrowser() && window?.adobeDataLayer ? window?.adobeDataLayer : undefined;
  }

  listennerForWindowsDL(): void {
    if (this.listenerKey) {
      return;
    }
    this.listenerKey = setInterval(() => {
      if (this.getWindowDL?.['version']) {
        clearInterval(this.listenerKey);
        if (this.tempAdobeDataLayer?.[0]) {
          // If the window data layer is available, it pushes the temporary data layer to it.
          window.adobeDataLayer.push(this.tempAdobeDataLayer[0]);
        }
      }
    }, 500);
  }

  async pageView(
    userData: UserDetails | undefined,
    pageViewData: Record<string, any>,
    permissionData: UserPermissions[],
    guestEmail?: string,
  ): Promise<void> {
    // Apply same guards as other event methods - pageView is not whitelisted for embedded
    if ((globals?.isEmbedded && !WHITE_LIST_EVENTS.includes('pageView')) || !isBrowser()) {
      return;
    }

    const page = this.getPageInfo();
    const user = await this.getUserInfo(userData, permissionData, guestEmail);

    // Store user data for use in subsequent events
    this.setUserData(userData, permissionData);

    this.removeEvents();

    // Build eventData object with page, user, and additional page data
    const eventData: any = {
      page,
      ...pageViewData,
    };

    // Only include user if it exists
    if (user && Object.keys(user).length > 0) {
      eventData.user = user;
    }

    // Use createEvent to maintain consistent structure with other events
    this.createEvent(eventData, 'pageView');
  }

  /**
   * Set current user data to be included in all subsequent events
   * Called automatically by pageView, but can be called manually when user state changes
   */
  setUserData(userData: UserDetails | undefined, permissionData?: UserPermissions[]): void {
    this.currentUserData = userData;
    this.currentPermissionData = permissionData;
  }

  /**
   * Clear stored user data (e.g., on logout)
   */
  clearUserData(): void {
    this.currentUserData = undefined;
    this.currentPermissionData = undefined;
  }

  // This method triggers a click event in the Adobe Data Layer.
  // It takes an event name and click object with properties for the click event.
  // It then pass the data to updateOrCreateEvent method.
  async clickEvent(event: string, click: DLClickEventProps, additionalData = {}): Promise<void> {
    const user = await this.getUserInfo(this.currentUserData, this.currentPermissionData);

    this.createEvent(
      {
        click,
        ...(user && Object.keys(user).length > 0 ? { user } : {}), // Only include if user is identifiable
        ...additionalData,
      },
      event,
    );
  }

  isPathMatched = (checkPath: string | string[] | undefined, path: string): boolean => {
    if (Array.isArray(checkPath)) {
      return checkPath.some(pathValue => path.includes(pathValue));
    }
    return Boolean(checkPath && path.includes(checkPath));
  };

  isSearchMatched = (checkSearch: string | undefined, searchContent: string): boolean =>
    Boolean(checkSearch && searchContent.includes(checkSearch));

  isUrlMatched = (checkURL: string | undefined, pageUrl: string): boolean =>
    Boolean(checkURL && pageUrl.includes(checkURL));

  brandInfo(): { country: string; brand: string } {
    if (globals.brandId === 'lwnz') {
      return {
        country: 'nz',
        brand: 'law',
      };
    }

    return {
      country: globals.country as string,
      brand: globals.brandId as string,
    };
  }

  // This method retrieves information about the current page from the window location.
  // It returns an object with properties for the page name, type, hier, country, and brand.
  // If the current path is the root ("/"), it sets the page name, type, and hier to "home".
  // Otherwise, it sets these properties to a cleaned-up version of the path.
  getPageInfo(): AnalyticPageInfo {
    let path = '',
      searchContent = '',
      pageUrl = '';
    if (isBrowser()) {
      ({ pathname: path, search: searchContent, href: pageUrl } = window.location);
    }
    const name = path?.replace(/[\W_]+/g, ' ')?.trim();

    // Default page info
    const page: AnalyticPageInfo = {
      pageName: '',
      pageType: '',
      pageHier: '',
      ...this.brandInfo(),
    };

    if (path === '/') {
      return { ...page, pageHier: 'home', pageName: 'homepage', pageType: 'home' };
    }

    const pageInfo = AdobeDataLayerMappings?.find(
      ({ checkPath, checkSearch, checkURL }) =>
        this.isPathMatched(checkPath, path) ||
        this.isSearchMatched(checkSearch, searchContent) ||
        this.isUrlMatched(checkURL, pageUrl),
    );

    if (pageInfo) {
      const { pageHier, pageType, pageName } = pageInfo,
        accountPages = pageHier.includes('account:');

      let resolvedPageName;

      if (pageName === 'path') {
        resolvedPageName = path;
      } else if (pageName) {
        resolvedPageName = pageName;
      } else {
        resolvedPageName = accountPages ? pageHier.replace(':', ' ') : name;
      }

      return {
        ...page,
        pageHier,
        pageType: pageType || (accountPages ? 'My Account' : name),
        pageName: resolvedPageName,
      };
    }

    // Default
    return { ...page, pageHier: name, pageName: name, pageType: name };
  }

  getPageType(): string {
    const pageInfo = this.getPageInfo();
    return pageInfo.pageType;
  }

  /**
   * Get user info for analytics tracking
   * Returns user data with hashed identifiers
   */
  async getUserInfo(
    loginUser?: UserDetails,
    permissionData?: UserPermissions[],
    guestEmail?: string,
  ): Promise<AnalyticUserInfo | undefined> {
    if (!isBrowser()) {
      return;
    }

    const { loginStatus, loginType } = userStatus(loginUser?.securityStatus);
    const user = this.createDefaultUserInfo(loginUser, loginStatus, loginType);

    if (this.isUserLoggedIn(loginType) && loginUser?.summaryDetails) {
      await this.enrichUserInfoForLoggedInUser(user, loginUser, permissionData);
    } else if (guestEmail) {
      // New (unidentified) customer: analytics requires the email captured at checkout.
      await this.addGuestEmailInfo(user, guestEmail);
    }

    return user;
  }

  /**
   * Add email info for a new (unidentified) customer, e.g. guest checkout confirmation.
   */
  private async addGuestEmailInfo(user: AnalyticUserInfo, email: string): Promise<void> {
    if (!email) {
      return;
    }
    const normalizedEmail = normalizeEmail(email);
    user.email = normalizedEmail;
    user.email_sha256 = await sha256Hash(normalizedEmail);
  }

  /**
   * Create default user info object
   */
  private createDefaultUserInfo(
    loginUser: UserDetails | undefined,
    loginStatus: string,
    loginType: string,
  ): AnalyticUserInfo {
    return {
      state: loginUser?.shoppingLocationContext?.stateCode || '',
      loginStatus,
      loginType: this.formatLoginTypeForAdobe(loginType),
      type: loginType === 'unidentified' ? 'New Customer' : 'Existing Customer',
    };
  }

  /**
   * Format login type for Adobe
   */
  private formatLoginTypeForAdobe(loginType: string): string {
    if (loginType === 'hard_logged') {
      return 'Hard Logged-In';
    }
    if (loginType === 'soft_logged') {
      return 'Soft Logged-In';
    }
    return 'unidentified';
  }

  /**
   * Check if user is logged in (soft or hard)
   */
  private isUserLoggedIn(loginType: string): boolean {
    return loginType === 'soft_logged' || loginType === 'hard_logged';
  }

  /**
   * Enrich user info with logged-in user details
   */
  private async enrichUserInfoForLoggedInUser(
    user: AnalyticUserInfo,
    loginUser: UserDetails,
    permissionData?: UserPermissions[],
  ): Promise<void> {
    const { loginType } = userStatus(loginUser?.securityStatus);
    const isHardLogged = loginType === 'hard_logged';

    this.addLegacyUserInfo(user, loginUser);

    const country = this.getUserCountry(loginUser);
    user.user_id = loginUser.summaryDetails.brandAccountNumber;
    user.country = country;

    await this.addEmailInfo(user, loginUser, isHardLogged);
    await this.addPhoneInfo(user, loginUser, country, isHardLogged);
    await this.addNameInfo(user, loginUser, isHardLogged);

    const billingAddress = this.getBillingAddress(loginUser);
    if (billingAddress) {
      await this.addAddressInfo(user, billingAddress, isHardLogged);
    }

    await this.addDOBInfo(user, loginUser, isHardLogged);
    this.addSubscriptionsInfo(user, loginUser);
    this.addPermissionsInfo(user, permissionData);
  }

  /**
   * Add legacy user info for backward compatibility
   */
  private addLegacyUserInfo(user: AnalyticUserInfo, loginUser: UserDetails): void {
    user.customerId = loginUser.summaryDetails.brandAccountNumber;
    user.eProfileId = loginUser.summaryDetails.profileId;
    user.salutation = loginUser.summaryDetails.salutation || '';
    user.name = `${loginUser.summaryDetails.firstName} ${loginUser.summaryDetails.lastName}`;
    user.type = 'Existing Customer';
    user.memberships = [];
    user.subscriptions = [];
  }

  /**
   * Get user's country for normalization
   * Priority: billingAddress.country > globals.country
   * Note: shoppingLocationContext.geographicType is only available for US market, not reliable for non-US markets
   */
  private getUserCountry(loginUser: UserDetails): string {
    const billingAddress = this.getBillingAddress(loginUser);
    const countryValue = billingAddress?.country || (globals.country as string);

    return normalizeCountry(countryValue);
  }

  /**
   * Add email information (normalized and hashed)
   * For soft-logged users: only SHA256 hash
   * For hard-logged users: both normalized email and SHA256 hash
   */
  private async addEmailInfo(user: AnalyticUserInfo, loginUser: UserDetails, isHardLogged: boolean): Promise<void> {
    if (!loginUser.summaryDetails.emailAddress) {
      return;
    }

    const normalizedEmail = normalizeEmail(loginUser.summaryDetails.emailAddress);

    if (isHardLogged) {
      user.email = normalizedEmail;
    }
    user.email_sha256 = await sha256Hash(normalizedEmail);
  }

  /**
   * Get billing address
   */
  private getBillingAddress(loginUser: UserDetails): any {
    return loginUser.trustedDetails?.billingAddress;
  }

  /**
   * Add phone information (normalized and hashed)
   * For soft-logged users: only SHA256 hash
   * For hard-logged users: both normalized phone and SHA256 hash
   */
  private async addPhoneInfo(
    user: AnalyticUserInfo,
    loginUser: UserDetails,
    country: string,
    isHardLogged: boolean,
  ): Promise<void> {
    const billingAddress = this.getBillingAddress(loginUser);
    if (!billingAddress?.dayPhoneNumber) {
      return;
    }

    const normalizedPhone = normalizePhone(billingAddress.dayPhoneNumber, country);

    if (isHardLogged) {
      user.phone = normalizedPhone;
    }
    user.phone_sha256 = await sha256Hash(normalizedPhone);
  }

  /**
   * Add name information (normalized and hashed)
   * For soft-logged users: only SHA256 hashes
   * For hard-logged users: both normalized names and SHA256 hashes
   */
  private async addNameInfo(user: AnalyticUserInfo, loginUser: UserDetails, isHardLogged: boolean): Promise<void> {
    if (loginUser.summaryDetails.firstName) {
      await this.addHashedField(
        user,
        'first_name',
        loginUser.summaryDetails.firstName.trim().toLowerCase(),
        isHardLogged,
      );
    }

    if (loginUser.summaryDetails.lastName) {
      await this.addHashedField(
        user,
        'last_name',
        loginUser.summaryDetails.lastName.trim().toLowerCase(),
        isHardLogged,
      );
    }
  }

  /**
   * Add address information (normalized and hashed)
   * For soft-logged users: only SHA256 hashes
   * For hard-logged users: both normalized address fields and SHA256 hashes
   * Note: Uses billingAddress.county when available, falls back to billingAddress.state
   * (UK addresses typically use county, US/AU addresses use state)
   */
  private async addAddressInfo(user: AnalyticUserInfo, billingAddress: any, isHardLogged: boolean): Promise<void> {
    if (billingAddress.address1) {
      const address1 = billingAddress.address1.trim().toLowerCase().replace(/\s+/g, ' '); //NOSONAR - replaceAll not available in ES6 target
      await this.addHashedField(user, 'address1', address1, isHardLogged);
    }

    if (billingAddress.city) {
      const city = billingAddress.city.trim().toLowerCase().replace(/\s+/g, ' '); //NOSONAR - replaceAll not available in ES6 target
      await this.addHashedField(user, 'city', city, isHardLogged);
    }

    // Use county when available (UK), fall back to state (US/AU)
    const regionField = billingAddress.county || billingAddress.state;
    if (regionField) {
      const region = regionField.trim().toLowerCase().replace(/\s+/g, ' '); //NOSONAR - replaceAll not available in ES6 target
      await this.addHashedField(user, 'region', region, isHardLogged);
    }

    if (billingAddress.postalCode) {
      const postcode = billingAddress.postalCode.trim().toLowerCase().replace(/[\s-]/g, ''); //NOSONAR - replaceAll not available in ES6 target
      await this.addHashedField(user, 'postcode', postcode, isHardLogged);
    }
  }

  /**
   * Add DOB information (normalized and hashed)
   * For soft-logged users: only SHA256 hash
   * For hard-logged users: both normalized DOB and SHA256 hash
   */
  private async addDOBInfo(user: AnalyticUserInfo, loginUser: UserDetails, isHardLogged: boolean): Promise<void> {
    const userDOB = loginUser.trustedDetails?.userDateOfBirth;
    if (!userDOB) {
      return;
    }

    const { year, month, day } = userDOB;
    if (!year || !month || !day) {
      return;
    }

    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dob = normalizeDOB(dateString);
    if (dob) {
      await this.addHashedField(user, 'dob', dob, isHardLogged);
    }
  }

  /**
   * Add subscriptions information (legacy format)
   */
  private addSubscriptionsInfo(user: AnalyticUserInfo, loginUser: UserDetails): void {
    const subscriptions = loginUser?.trustedDetails?.subscriptions;
    if (!subscriptions || subscriptions.length === 0) {
      return;
    }

    for (const sub of subscriptions) {
      const subscriptionData = {
        schemeCode: sub.schemeId,
        type: sub.type,
        startDate: sub.enrollmentFromDateAsString,
        endDate: sub.enrollmentToDateAsString,
        frequency: sub.frequency,
        status: sub.status,
      };

      if (sub?.type === 'WINEPLAN') {
        user.subscriptions.push(subscriptionData);
      } else if (sub?.type !== 'WINEPLAN') {
        user.memberships.push(subscriptionData);
      }
    }
  }

  /**
   * Add permissions information
   */
  private addPermissionsInfo(user: AnalyticUserInfo, permissionData?: UserPermissions[]): void {
    if (!permissionData) {
      return;
    }

    const result = permissionData
      .map((item: { code: string; state: string }) => `${item.code}=${item.state}`)
      .join(':');
    user.marketingPermissions = result;
  }

  /**
   * Helper to add a field and its SHA256 hash
   * For soft-logged users: only adds SHA256 hash (no plaintext)
   * For hard-logged users: adds both normalized value and SHA256 hash
   */
  private async addHashedField(user: any, fieldName: string, value: string, isHardLogged: boolean): Promise<void> {
    if (isHardLogged) {
      user[fieldName] = value;
    }
    user[`${fieldName}_sha256`] = await sha256Hash(value);
  }

  async preferenceSavedDLEvent(): Promise<void> {
    const user = await this.getUserInfo(this.currentUserData, this.currentPermissionData);

    this.createEvent(
      {
        ...DL_EVENTS_CONFIGURATION.ACCOUNT_PREFERENCE_SAVED,
        ...(user && Object.keys(user).length > 0 ? { user } : {}),
      },
      DL_EVENTS_CONFIGURATION.ACCOUNT_PREFERENCE_SAVED.action,
    );
  }

  // Checkout Step 4 - Purchase click event, fired on the confirmation page load.
  async checkoutPurchaseEvent(): Promise<void> {
    const { event, ...click } = DL_EVENTS_CONFIGURATION.CHECKOUT_STEP_4_PURCHASE;
    await this.clickEvent(event, click);
  }

  // Basket update DL for cart page
  async basketUpdate(productInformation: DLBasketUpdateProps[]): Promise<void> {
    const basketArray = productInformation.map(({ itemCode, name, webHeadline, skus, cartQuantity }) => ({
      itemCode,
      productName: name,
      webHeadline,
      unitPrice: skus?.[0]?.salePrice,
      qty: cartQuantity,
    }));

    const user = await this.getUserInfo(this.currentUserData, this.currentPermissionData);

    this.createEvent(
      {
        basket: basketArray,
        ...(user && Object.keys(user).length > 0 ? { user } : {}),
      },
      'basketUpdate',
    );
  }

  async basketDetails(cartDetails: DLBasketDetailsProps): Promise<void> {
    const user = await this.getUserInfo(this.currentUserData, this.currentPermissionData);

    this.createEvent(
      {
        ...cartDetails,
        ...(user && Object.keys(user).length > 0 ? { user } : {}),
      },
      'basketDetails',
    );
  }
  async subscribeAndSaveProductDetails(): Promise<void> {
    const SHOW_SUBSCRIBE_AND_SAVE = 'Show Subscribe and Save';
    const ssDetails = {
      clickDescription: `Product:${SHOW_SUBSCRIBE_AND_SAVE}`,
      clickName: SHOW_SUBSCRIBE_AND_SAVE,
      clickLocation: 'Product',
      clickText: SHOW_SUBSCRIBE_AND_SAVE,
      clickAction: SHOW_SUBSCRIBE_AND_SAVE,
    };

    const user = await this.getUserInfo(this.currentUserData, this.currentPermissionData);

    this.createEvent(
      {
        ...ssDetails,
        ...(user && Object.keys(user).length > 0 ? { user } : {}),
      },
      'SubscribeSaveProduct',
    );
  }

  /**
   * Add to cart event
   */
  async addToCart(productData: Record<string, any>): Promise<void> {
    await this.pushEvent('addToCart', productData);
  }

  async ribbonClickEvent(click: DLClickEventProps, promotion: DLRibbonPromotionProps): Promise<void> {
    await this.pushEvent('ribbonClick', { click, promotion }, false);
  }

  private removeEvents() {
    if (Array.isArray(window.adobeDataLayer)) {
      // Loop through the array in reverse order (to avoid skipping elements when removing)
      for (let i = window.adobeDataLayer.length - 1; i >= 0; i--) {
        if (datalayerCleanup.has(window.adobeDataLayer[i].event)) {
          window.adobeDataLayer.splice(i, 1); // Remove the element at index i
        }
      }
    }
  }

  public removeEvent(eventName: string): void {
    if (Array.isArray(window.adobeDataLayer)) {
      // Loop through the array in reverse order (to avoid skipping elements when removing)
      for (let i = window.adobeDataLayer.length - 1; i >= 0; i--) {
        if (window.adobeDataLayer[i].event === eventName) {
          window.adobeDataLayer.splice(i, 1); // Remove the element at index i
        }
      }
    }
  }

  // This method creates a new event in the data layer.
  private createEvent(eventData: any, event: string, propName = 'eventData') {
    if ((globals?.isEmbedded && !WHITE_LIST_EVENTS.includes(event)) || !isBrowser()) {
      return;
    }

    // If the window data layer is available, it push the data to window data layer otherwise it push the data to temporary data layer.
    if (this.getWindowDL) {
      window.adobeDataLayer?.push({ event, [propName]: eventData });
    } else {
      this.tempAdobeDataLayer?.push({ event, [propName]: eventData });
    }
  }

  /**
   * Push event with flat structure (user and data at root level)
   * This is the new format for identity data layer events
   */
  private async pushEvent(event: string, data: Record<string, any> = {}, includeUser = true): Promise<void> {
    if ((globals?.isEmbedded && !WHITE_LIST_EVENTS.includes(event)) || !isBrowser()) {
      return;
    }

    const eventPayload: any = {
      event,
      ...data,
    };

    // Include user data if requested and available
    if (includeUser) {
      const user = await this.getUserInfo(this.currentUserData, this.currentPermissionData);
      if (user && Object.keys(user).length > 0) {
        eventPayload.user = user;
      }
    }

    if (this.getWindowDL) {
      window.adobeDataLayer?.push(eventPayload);
    } else {
      this.tempAdobeDataLayer?.push(eventPayload);
    }
  }
}

export const adobeDataLayer = new AdobeDataLayer();
